import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { GateMonitoringView } from '../gate/GateMonitoringView';
import { DeliveryQueueView } from '../delivery-queue/DeliveryQueueView';
import { MobileVisualTableMap } from './MobileVisualTableMap';

interface DelegatePanelViewProps {
  onSwitchPanel?: (panel: 'lookup' | 'check-in') => void;
}

interface TableGuest {
  id: string;
  fullName: string;
  ticketNumber: string;
  seatNumber: number;
  status: string;
  dietaryPreference: string | null;
}

interface GuestResult {
  id: string;
  fullName: string;
  ticketNumber: string;
  chapterAffiliation: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
}

interface VendorBooth {
  id: string;
  boothNumber: string | null;
  zone: string | null;
  description: string | null;
  vendor: {
    id: string;
    businessName: string;
    category: string | null;
    contactPerson?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  event: {
    id: string;
    name: string;
  };
}

interface VendorDetail {
  id: string;
  businessName: string;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  category: string | null;
  mailingAddress: string | null;
  eventRegistrations: Array<{
    id: string;
    boothNumber: string | null;
    zone: string | null;
    description: string | null;
    status: string;
    event: { name: string };
  }>;
}

const containerStyle: React.CSSProperties = {
  padding: '20px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  paddingBottom: '100px',
};

const headingStyle: React.CSSProperties = {
  fontSize: '1.3rem',
  fontWeight: 700,
  color: '#fff',
  margin: '0 0 8px 0',
};

const actionBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  padding: '18px 16px',
  border: 'none',
  borderRadius: '12px',
  background: '#1a2e23',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  minHeight: '56px',
  textAlign: 'left',
  WebkitTapHighlightColor: 'transparent',
};

const iconSpanStyle: React.CSSProperties = {
  fontSize: '1.4rem',
};

const searchContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '14px',
  background: '#1a2e23',
  borderRadius: '12px',
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '8px',
  border: '1px solid #2a4a35',
  background: '#0f1f17',
  color: '#fff',
  fontSize: '0.95rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const resultItemStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: '#0f1f17',
  borderRadius: '8px',
  color: '#e0e0e0',
  fontSize: '0.9rem',
};

const statusBadge = (status: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 600,
  marginLeft: '6px',
  background: status === 'CheckedIn' ? '#065f46' : status === 'Assigned' ? '#1e3a5f' : '#3b3b3b',
  color: status === 'CheckedIn' ? '#6ee7b7' : status === 'Assigned' ? '#93c5fd' : '#aaa',
});

export function DelegatePanelView({ }: DelegatePanelViewProps) {
  const navigate = useNavigate();

  // Guest search state
  const [showGuestSearch, setShowGuestSearch] = useState(false);
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  const [guestResults, setGuestResults] = useState<GuestResult[]>([]);
  const [guestSearching, setGuestSearching] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Table lookup state
  const [showTableSearch, setShowTableSearch] = useState(false);
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [tableGuests, setTableGuests] = useState<TableGuest[]>([]);
  const [tableSearching, setTableSearching] = useState(false);
  const [tableError, setTableError] = useState('');
  const [matchedTableName, setMatchedTableName] = useState('');

  // Vendor directory state
  const [showVendors, setShowVendors] = useState(false);
  const [vendors, setVendors] = useState<VendorBooth[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorsLoaded, setVendorsLoaded] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorDetail | null>(null);
  const [vendorView, setVendorView] = useState<'all' | 'category' | 'event' | 'zone'>('all');
  const [vendorDrilldown, setVendorDrilldown] = useState<string | null>(null);

  // GX Journey state
  const [showJourney, setShowJourney] = useState(false);
  const [journeyTasks, setJourneyTasks] = useState<Array<{ id: string; title: string; status: string; priority: string; eventName: string; eventTime: string; dateLabel: string; subcommittee: string | null }>>([]);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeyLoaded, setJourneyLoaded] = useState(false);

  // Reports state
  const [showReports, setShowReports] = useState(false);
  const [reportsView, setReportsView] = useState<'menu' | 'summary' | 'detail' | 'pickup-stats' | 'key-members' | 'visual-map' | 'table-requests'>('menu');
  const [reportDisplayMode, setReportDisplayMode] = useState<'cards' | 'table'>('cards');
  const [chapterDistData, setChapterDistData] = useState<Array<{ chapter: string; inceptionYear: number | null; totalGuests: number; seatedGuests: number; unseatedGuests: number; tableCount: number; zoneCount: number; guests: Array<{ fullName: string; ticketNumber: string | null; orderReferenceId: string | null; tableName: string | null; tableNumber: number | null; seatNumber: number | null; zoneName: string | null; isSeated: boolean }> }>>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [selectedReportChapter, setSelectedReportChapter] = useState<string | null>(null);
  const [pickupStats, setPickupStats] = useState<{ byLocation: Record<string, number>; totalPickups: number; totalExpected: number } | null>(null);
  const [keyMembersData, setKeyMembersData] = useState<Array<{ name: string; role: string; chapter: string; tableName: string | null; tableNumber: number | null; zoneName: string | null; isSeated: boolean; hasTicket: boolean }> | null>(null);
  const [keyMembersDisplay, setKeyMembersDisplay] = useState<'table' | 'cards'>('table');
  const [tableRequestsData, setTableRequestsData] = useState<Array<{ id: string; name: string; status: string; memberCount: number; tables: number[]; seats: number[]; allSeated: boolean }> | null>(null);
  const [keyMembersTicketFilter, setKeyMembersTicketFilter] = useState<'all' | 'with-ticket' | 'no-ticket'>('all');

  // Gate monitoring state
  const [showGateMonitor, setShowGateMonitor] = useState(false);
  const [gateEvents, setGateEvents] = useState<Array<{ id: string; name: string; date: string }>>([]);
  const [gateEventsLoading, setGateEventsLoading] = useState(false);
  const [gateSelectedEventId, setGateSelectedEventId] = useState<string | null>(null);
  const [liveEventIds, setLiveEventIds] = useState<Set<string>>(new Set());

  // Packet Pickup state
  const [showPacketPickup, setShowPacketPickup] = useState(false);
  const [showDeliveryQueue, setShowDeliveryQueue] = useState(false);
  const [ppLocation, setPpLocation] = useState<string | null>(() => {
    try { return localStorage.getItem('packetPickupLocation'); } catch { return null; }
  });
  const [ppSearchTerm, setPpSearchTerm] = useState('');
  const [ppSearching, setPpSearching] = useState(false);
  const [ppResults, setPpResults] = useState<Array<{ guestRecordId: string; fullName: string; ticketNumber: string | null; chapterAffiliation: string | null; isCheckedIn: boolean }>>([]);
  const [ppConfirming, setPpConfirming] = useState<string | null>(null);
  const [ppMessage, setPpMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Guest search handler — uses ?query= param (matches backend)
  async function handleGuestSearch() {
    if (!guestSearchTerm.trim()) return;
    setGuestSearching(true);
    setSelectedGuest(null);
    try {
      const data = await apiClient<any>(
        `/table-management/guests?query=${encodeURIComponent(guestSearchTerm.trim())}`
      );
      const results = Array.isArray(data) ? data : (data?.guests || []);
      setGuestResults(results);
    } catch {
      setGuestResults([]);
    } finally {
      setGuestSearching(false);
    }
  }

  // Guest detail handler — fetch detail on tap
  async function handleGuestTap(guest: GuestResult) {
    setLoadingDetail(true);
    try {
      const detail = await apiClient<any>(`/table-management/guests/${guest.id}`);
      setSelectedGuest(detail);
    } catch {
      // Show basic info from search result if detail fails
      setSelectedGuest(guest);
    } finally {
      setLoadingDetail(false);
    }
  }

  // Table lookup handler — uses guest search API and groups by table
  async function handleTableSearch() {
    const term = tableSearchTerm.trim();
    if (!term) {
      setTableError('Enter a table name or number');
      return;
    }
    setTableSearching(true);
    setTableError('');
    setTableGuests([]);
    setMatchedTableName('');
    try {
      // First try to fetch tables list directly
      let found = false;
      try {
        const tables = await apiClient<any[]>('/table-management/tables');
        const tableList = Array.isArray(tables) ? tables : [];
        const match = tableList.find((t: any) => {
          const name = (t.tableName || '').toLowerCase();
          const num = String(t.tableNumber || '');
          return name.toLowerCase().includes(term.toLowerCase()) || num === term;
        });

        if (match) {
          setMatchedTableName(match.tableName || `Table ${match.tableNumber}`);
          // The listTables response includes seats with guests
          if (match.seats && Array.isArray(match.seats)) {
            const seats: TableGuest[] = match.seats.map((s: any) => ({
              id: s.id,
              fullName: s.guest?.fullName || (s.status === 'Available' ? '(Empty)' : 'Unassigned'),
              ticketNumber: s.guest?.ticketNumber || '',
              seatNumber: s.seatNumber,
              status: s.status || 'Available',
              dietaryPreference: s.guest?.dietaryPreference || null,
            }));
            setTableGuests(seats);
            found = true;
          } else {
            // Try fetching table detail
            const detail = await apiClient<any>(`/table-management/tables/${match.id}`);
            if (detail && Array.isArray(detail.seats)) {
              const seats: TableGuest[] = detail.seats.map((s: any) => ({
                id: s.id,
                fullName: s.guestName || (s.status === 'Available' ? '(Empty)' : 'Unassigned'),
                ticketNumber: s.guestTicketNumber || '',
                seatNumber: s.seatNumber,
                status: s.status || 'Available',
                dietaryPreference: s.guestMeal || null,
              }));
              setTableGuests(seats);
              found = true;
            }
          }
        }
      } catch {
        // Tables endpoint failed — fall through to guest search approach
      }

      if (!found) {
        // Fallback: search guests with table name as query
        try {
          const data = await apiClient<any>(`/table-management/guests?query=${encodeURIComponent(term)}`);
          const guests = Array.isArray(data) ? data : (data?.guests || []);
          if (guests.length > 0) {
            setMatchedTableName(`Results for "${term}"`);
            const seats: TableGuest[] = guests.slice(0, 20).map((g: any) => ({
              id: g.id,
              fullName: g.fullName || 'Unknown',
              ticketNumber: g.ticketNumber || '',
              seatNumber: g.seatNumber || 0,
              status: g.checkInStatus || 'Assigned',
              dietaryPreference: g.dietaryPreference || null,
            }));
            setTableGuests(seats);
            found = true;
          }
        } catch {
          // Guest search also failed
        }
      }

      if (!found) {
        setTableError(`No table or guests found matching "${term}"`);
      }
    } catch {
      setTableError('Could not look up table');
      setTableGuests([]);
    } finally {
      setTableSearching(false);
    }
  }

  // Vendor directory handler — loads booth locations on first open
  async function handleOpenVendors() {
    const toggling = !showVendors;
    setShowVendors(toggling);
    setShowGuestSearch(false);
    setShowTableSearch(false);
    setShowJourney(false);
    setShowGateMonitor(false);
    setSelectedVendor(null);
    if (toggling && !vendorsLoaded) {
      setVendorsLoading(true);
      try {
        // Fetch all vendors (accessible to all authenticated users)
        const allVendors = await apiClient<any[]>('/vendors/directory');
        const vendorList = Array.isArray(allVendors) ? allVendors : [];
        // Map to VendorBooth-like shape for display
        const mapped: VendorBooth[] = vendorList.map((v: any) => {
          const reg = v.eventRegistrations?.[0];
          return {
            id: reg?.id || v.id,
            boothNumber: reg?.boothNumber || null,
            zone: reg?.zone || null,
            description: reg?.description || null,
            vendor: { id: v.id, businessName: v.businessName, category: v.category || null, contactPerson: v.contactPerson || null, email: v.email || null, phone: v.phone || null },
            event: reg?.event || { id: '', name: '—' },
          };
        });
        setVendors(mapped);
        setVendorsLoaded(true);
      } catch {
        setVendors([]);
        setVendorsLoaded(true);
      } finally {
        setVendorsLoading(false);
      }
    }
  }

  // Fetch full vendor detail on row tap — uses local data since /vendors/:id requires Organizer
  function handleVendorTap(vendorId: string) {
    // Build detail from local booth-locations data
    const booths = vendors.filter((v) => v.vendor.id === vendorId);
    if (booths.length === 0) return;
    const first = booths[0];
    setSelectedVendor({
      id: first.vendor.id,
      businessName: first.vendor.businessName,
      contactPerson: null,
      email: null,
      phone: null,
      category: first.vendor.category,
      mailingAddress: null,
      eventRegistrations: booths.map((b) => ({
        id: b.id,
        boothNumber: b.boothNumber,
        zone: b.zone,
        description: b.description,
        status: 'Confirmed',
        event: { name: b.event.name },
      })),
    });
  }

  // GX Journey handler — loads weekend tasks on first open
  async function handleOpenJourney() {
    const toggling = !showJourney;
    setShowJourney(toggling);
    setShowGuestSearch(false);
    setShowTableSearch(false);
    setShowVendors(false);
    if (toggling && !journeyLoaded) {
      setJourneyLoading(true);
      try {
        const [eventsData, tasksData] = await Promise.all([
          apiClient<Array<{ id: string; name: string; date: string; startTime: string; endTime: string }>>('/events'),
          apiClient<Array<{ id: string; title: string; status: string; priority: string; category: string; dueDate: string; event?: { id: string; name: string }; subcommittee?: { id: string; name: string } }>>('/event-tasks').catch(() => []),
        ]);
        const events = Array.isArray(eventsData) ? eventsData : [];
        const tasks = Array.isArray(tasksData) ? tasksData : [];
        const eventMap = new Map(events.map((e) => [e.id, e]));

        const fmtTime = (s: string) => {
          if (!s) return '';
          const h = s.includes('T') ? Number(s.split('T')[1].slice(0, 2)) : Number(s.split(':')[0]);
          const m = s.includes('T') ? Number(s.split('T')[1].slice(3, 5)) : Number(s.split(':')[1]);
          const p = h >= 12 ? 'PM' : 'AM';
          return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${p}`;
        };
        const fmtDate = (d: string) => {
          if (!d) return '';
          const part = d.includes('T') ? d.split('T')[0] : d;
          const [y, mo, da] = part.split('-').map(Number);
          return new Date(y, mo - 1, da).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        };

        const weekendTasks = tasks.filter((t) => t.category === 'Weekend');
        const mapped = weekendTasks.map((t) => {
          const evt = t.event ? eventMap.get(t.event.id) : null;
          return {
            id: t.id,
            title: t.title,
            status: t.status,
            priority: t.priority || 'Medium',
            eventName: evt?.name || t.event?.name || 'General',
            eventTime: evt ? `${fmtTime(evt.startTime)} – ${fmtTime(evt.endTime)}` : '',
            dateLabel: fmtDate(evt?.date || t.dueDate),
            subcommittee: t.subcommittee?.name || null,
          };
        });

        const priorityOrder: Record<string, number> = { High: 0, Medium: 1, Low: 2 };
        mapped.sort((a, b) => (a.dateLabel.localeCompare(b.dateLabel)) || ((priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1)));

        setJourneyTasks(mapped);
        setJourneyLoaded(true);
      } catch {
        setJourneyTasks([]);
      } finally {
        setJourneyLoading(false);
      }
    }
  }

  async function handleOpenGateMonitor() {
    const toggling = !showGateMonitor;
    setShowGateMonitor(toggling);
    setGateSelectedEventId(null);
    setShowGuestSearch(false);
    setShowTableSearch(false);
    setShowVendors(false);
    setShowJourney(false);
    if (toggling && gateEvents.length === 0) {
      setGateEventsLoading(true);
      try {
        const data = await apiClient<Array<{ id: string; name: string; date: string }>>('/events');
        const events = Array.isArray(data) ? data : [];
        setGateEvents(events);
        // Check which events have active gate sessions
        const liveIds = new Set<string>();
        await Promise.all(
          events.map(async (evt) => {
            try {
              const counts = await apiClient<{ hasActiveSession: boolean }>(`/gate/events/${evt.id}/counts`);
              if (counts.hasActiveSession) liveIds.add(evt.id);
            } catch { /* ignore — event may not have gate config */ }
          })
        );
        setLiveEventIds(liveIds);
      } catch {
        setGateEvents([]);
      } finally {
        setGateEventsLoading(false);
      }
    }
  }

  return (
    <div style={containerStyle}>
      <h2 style={headingStyle}>⚡ Delegate Panel</h2>

      {/* Guest Lookup — inline search */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={() => { setShowGuestSearch((s) => !s); setShowTableSearch(false); setShowVendors(false); setShowJourney(false); }}
        aria-label="Guest Lookup"
      >
        <span style={iconSpanStyle} aria-hidden="true">🔍</span>
        <span>Guest Lookup</span>
      </button>

      {showGuestSearch && (
        <div style={searchContainerStyle}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              style={searchInputStyle}
              placeholder="Search by name or ticket number..."
              value={guestSearchTerm}
              onChange={(e) => setGuestSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleGuestSearch(); }}
              aria-label="Search guests"
            />
            {guestSearchTerm && (
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.2rem', padding: '0 8px' }}
                onClick={() => { setGuestSearchTerm(''); setGuestResults([]); setSelectedGuest(null); }}
                aria-label="Clear search"
              >✕</button>
            )}
            <button
              type="button"
              style={{ ...actionBtnStyle, width: 'auto', padding: '10px 16px', minHeight: 'unset', fontSize: '0.9rem' }}
              onClick={handleGuestSearch}
              disabled={guestSearching}
            >
              {guestSearching ? '...' : 'Go'}
            </button>
          </div>
          {guestResults.length > 0 && !selectedGuest && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto' }}>
              {guestResults.map((g, i) => (
                <button
                  key={g.id || i}
                  type="button"
                  style={{ ...resultItemStyle, cursor: 'pointer', border: 'none', textAlign: 'left', width: '100%' }}
                  onClick={() => handleGuestTap(g)}
                  aria-label={`View details for ${g.fullName}`}
                >
                  <strong>{g.fullName}</strong>
                  {g.ticketNumber && <span style={{ marginLeft: '8px', color: '#c9a84c' }}>{g.ticketNumber}</span>}
                  {g.chapterAffiliation && <span style={{ marginLeft: '8px', color: '#888' }}>{g.chapterAffiliation}</span>}
                </button>
              ))}
            </div>
          )}
          {loadingDetail && <p style={{ color: '#c9a84c', fontSize: '0.85rem', margin: 0 }}>Loading...</p>}
          {selectedGuest && (
            <div style={{ background: '#0f1f17', borderRadius: '10px', padding: '14px', border: '1px solid #2a4a35' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <strong style={{ fontSize: '1rem', color: '#fff' }}>{selectedGuest.fullName}</strong>
                <button type="button" style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.1rem' }} onClick={() => setSelectedGuest(null)} aria-label="Close detail">✕</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '0.85rem', color: '#ccc' }}>
                {/* Primary info — large and prominent */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px 12px', background: '#0a1a12', borderRadius: '8px', marginBottom: '6px' }}>
                  {selectedGuest.ticketNumber && <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff' }}>🎫 {selectedGuest.ticketNumber}{selectedGuest.registration?.ticket?.tierName ? ` — ${selectedGuest.registration.ticket.tierName}` : ''}</div>}
                  {selectedGuest.seat?.[0] && <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff' }}>🪑 {selectedGuest.seat[0].table?.tableName || `Table ${selectedGuest.seat[0].table?.tableNumber}`}, Seat {selectedGuest.seat[0].seatNumber}</div>}
                  {selectedGuest.seat?.[0]?.table?.zone?.name && <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff' }}>🏷️ {selectedGuest.seat[0].table.zone.name}</div>}
                  {selectedGuest.registration?.ticket?.order?.referenceId && <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff' }}>📦 {selectedGuest.registration.ticket.order.referenceId}</div>}
                </div>
                {/* Secondary info — normal size */}
                {selectedGuest.chapterAffiliation && <div>📍 <strong>Chapter:</strong> {selectedGuest.chapterAffiliation}</div>}
                {selectedGuest.dietaryPreference && <div>🍽️ <strong>Meal:</strong> {selectedGuest.dietaryPreference}</div>}
                {selectedGuest.rsvpStatus && <div>📋 <strong>RSVP:</strong> {selectedGuest.rsvpStatus}</div>}
                {selectedGuest.paymentStatus && <div>💳 <strong>Payment:</strong> {selectedGuest.paymentStatus}</div>}
                {selectedGuest.accessibilityNeeds && <div>♿ <strong>Accessibility:</strong> {selectedGuest.accessibilityNeeds}</div>}
                {selectedGuest.familyAssociation && <div>👨‍👩‍👧 <strong>Family:</strong> {selectedGuest.familyAssociation}</div>}
                {/* Check-In & Packet Pickup */}
                {selectedGuest.registration?.checkIn ? (
                  <>
                    <div>✅ <strong>Checked In:</strong> {new Date(selectedGuest.registration.checkIn.checkedInAt).toLocaleString()}</div>
                    {selectedGuest.registration.checkIn.pickupLocation && (
                      <div>📦 <strong>Packet Pickup:</strong> {selectedGuest.registration.checkIn.pickupLocation}</div>
                    )}
                    {!selectedGuest.registration.checkIn.pickupLocation && (
                      <div>📦 <strong>Packet:</strong> <span style={{ color: '#f59e0b' }}>Not picked up</span></div>
                    )}
                  </>
                ) : (
                  <div>⬜ <strong>Check-In:</strong> <span style={{ color: '#f87171' }}>Not checked in</span></div>
                )}
              </div>
            </div>
          )}
          {guestResults.length === 0 && guestSearchTerm && !guestSearching && (
            <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>No results found.</p>
          )}
        </div>
      )}

      {/* Table Lookup — inline table name search */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={() => { setShowTableSearch((s) => !s); setShowGuestSearch(false); }}
        aria-label="Table Lookup"
      >
        <span style={iconSpanStyle} aria-hidden="true">🪑</span>
        <span>Table Lookup</span>
      </button>

      {showTableSearch && (
        <div style={searchContainerStyle}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              style={searchInputStyle}
              placeholder="Table name or number..."
              value={tableSearchTerm}
              onChange={(e) => setTableSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleTableSearch(); }}
              aria-label="Table name"
            />
            <button
              type="button"
              style={{ ...actionBtnStyle, width: 'auto', padding: '10px 16px', minHeight: 'unset', fontSize: '0.9rem' }}
              onClick={handleTableSearch}
              disabled={tableSearching}
            >
              {tableSearching ? '...' : 'Find'}
            </button>
          </div>
          {tableError && <p style={{ color: '#f87171', fontSize: '0.85rem', margin: 0 }}>{tableError}</p>}
          {tableGuests.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '350px', overflowY: 'auto' }}>
              <div style={{ fontSize: '0.8rem', color: '#c9a84c', fontWeight: 600, marginBottom: '4px' }}>
                {matchedTableName} — {tableGuests.length} seat(s)
              </div>
              {tableGuests.map((g, i) => (
                <div key={g.id || i} style={resultItemStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><strong>Seat {g.seatNumber}</strong> — {g.fullName}</span>
                    <span style={statusBadge(g.status)}>{g.status}</span>
                  </div>
                  {g.ticketNumber && <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>{g.ticketNumber}</div>}
                  {g.dietaryPreference && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>🍽️ {g.dietaryPreference}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Packet Pickup — inline */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={() => { setShowPacketPickup((s) => !s); setShowGuestSearch(false); setShowTableSearch(false); setShowVendors(false); setShowJourney(false); setShowGateMonitor(false); }}
        aria-label="Packet Pickup"
      >
        <span style={iconSpanStyle} aria-hidden="true">📦</span>
        <span>Packet Pickup</span>
      </button>

      {showPacketPickup && (
        <div style={searchContainerStyle}>
          {/* Location selector — large pills */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {['Hotel', 'Symposium', 'Meet_and_Greet', 'Ball_Door'].map((loc) => {
              const labels: Record<string, string> = { Hotel: '🏨 Hotel', Symposium: '🎓 Symposium', Meet_and_Greet: '🤝 Meet & Greet', Ball_Door: '🚪 Ball Door' };
              const isActive = ppLocation === loc;
              return (
                <button
                  key={loc}
                  type="button"
                  style={{
                    padding: '12px 18px',
                    borderRadius: '20px',
                    border: isActive ? '2px solid #c9a84c' : '1px solid #2a5a3e',
                    background: isActive ? '#2a4a35' : '#0f1f17',
                    color: isActive ? '#c9a84c' : '#a0a0a0',
                    fontSize: '1rem',
                    fontWeight: isActive ? 700 : 500,
                    cursor: 'pointer',
                    minHeight: '48px',
                  }}
                  onClick={() => { setPpLocation(loc); try { localStorage.setItem('packetPickupLocation', loc); } catch {} }}
                  aria-pressed={isActive}
                >
                  {labels[loc]}
                </button>
              );
            })}
          </div>
          {!ppLocation && (
            <p style={{ color: '#f59e0b', fontSize: '0.8rem', margin: '0 0 8px 0' }}>⚠️ Select a location first</p>
          )}

          {/* Search */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              style={searchInputStyle}
              placeholder="Ticket #, name, or scan QR..."
              value={ppSearchTerm}
              onChange={(e) => setPpSearchTerm(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && ppSearchTerm.trim()) {
                  setPpSearching(true); setPpMessage(null);
                  try {
                    const data = await apiClient<{ results: typeof ppResults }>(`/packet-pickup/search?q=${encodeURIComponent(ppSearchTerm.trim())}`);
                    setPpResults(data.results || []);
                    if ((data.results || []).length === 0) setPpMessage({ type: 'error', text: 'No guests found.' });
                  } catch { setPpResults([]); setPpMessage({ type: 'error', text: 'Search failed.' }); }
                  finally { setPpSearching(false); }
                }
              }}
              aria-label="Search guest for packet pickup"
            />
            <button
              type="button"
              style={{ ...actionBtnStyle, width: 'auto', padding: '10px 16px', minHeight: 'unset', fontSize: '0.9rem' }}
              disabled={ppSearching || !ppSearchTerm.trim()}
              onClick={async () => {
                if (!ppSearchTerm.trim()) return;
                setPpSearching(true); setPpMessage(null);
                try {
                  const data = await apiClient<{ results: typeof ppResults }>(`/packet-pickup/search?q=${encodeURIComponent(ppSearchTerm.trim())}`);
                  setPpResults(data.results || []);
                  if ((data.results || []).length === 0) setPpMessage({ type: 'error', text: 'No guests found.' });
                } catch { setPpResults([]); setPpMessage({ type: 'error', text: 'Search failed.' }); }
                finally { setPpSearching(false); }
              }}
            >
              {ppSearching ? '...' : 'Go'}
            </button>
          </div>

          {/* Messages */}
          {ppMessage && (
            <p style={{ color: ppMessage.type === 'success' ? '#4caf50' : '#f87171', fontSize: '0.82rem', margin: '8px 0 0' }}>{ppMessage.text}</p>
          )}

          {/* Results */}
          {ppResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px', maxHeight: '400px', overflowY: 'auto' }}>
              {ppResults.map((g) => (
                <div key={g.guestRecordId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: '#0f1f17', borderRadius: '12px', border: '1px solid #1a3d2a' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.1rem' }}>{g.fullName}</div>
                    <div style={{ fontSize: '0.9rem', color: '#c9a84c', marginTop: '2px', fontWeight: 600 }}>
                      {g.ticketNumber || '—'}{g.chapterAffiliation ? ` • ${g.chapterAffiliation}` : ''}
                    </div>
                  </div>
                  {g.isCheckedIn ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '1rem', color: '#4caf50', fontWeight: 700 }}>✅ Done</span>
                      <button
                        type="button"
                        style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #f87171', background: 'transparent', color: '#f87171', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                        onClick={async () => {
                          setPpMessage(null);
                          try {
                            await apiClient('/packet-pickup/reverse', { method: 'POST', body: { guestRecordId: g.guestRecordId } });
                            setPpResults((prev) => prev.map((r) => r.guestRecordId === g.guestRecordId ? { ...r, isCheckedIn: false } : r));
                            setPpMessage({ type: 'success', text: `↩️ ${g.fullName} — Check-in reversed` });
                          } catch (err: any) {
                            setPpMessage({ type: 'error', text: err?.message || 'Reverse failed' });
                          }
                        }}
                        aria-label={`Reverse check-in for ${g.fullName}`}
                      >
                        Undo
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      style={{
                        padding: '14px 24px',
                        borderRadius: '10px',
                        border: 'none',
                        background: ppLocation ? '#c9a84c' : '#555',
                        color: '#000',
                        fontWeight: 800,
                        fontSize: '1.1rem',
                        cursor: ppLocation ? 'pointer' : 'not-allowed',
                        opacity: ppConfirming === g.guestRecordId ? 0.6 : 1,
                        minHeight: '52px',
                        minWidth: '110px',
                      }}
                      disabled={!ppLocation || ppConfirming === g.guestRecordId}
                      onClick={async () => {
                        if (!ppLocation) return;
                        setPpConfirming(g.guestRecordId); setPpMessage(null);
                        try {
                          await apiClient('/packet-pickup', { method: 'POST', body: { guestRecordId: g.guestRecordId, pickupLocation: ppLocation } });
                          setPpResults((prev) => prev.map((r) => r.guestRecordId === g.guestRecordId ? { ...r, isCheckedIn: true } : r));
                          setPpMessage({ type: 'success', text: `✅ ${g.fullName} — Packet confirmed` });
                          // Enqueue to delivery queue (fire-and-forget)
                          apiClient('/packet-pickup/queue', { method: 'POST', body: { guestName: g.fullName, ticketNumber: g.ticketNumber || null, orderNumber: null, location: ppLocation || 'Unknown' } }).catch(() => {});
                        } catch (err: any) {
                          if (err?.statusCode === 409) {
                            setPpResults((prev) => prev.map((r) => r.guestRecordId === g.guestRecordId ? { ...r, isCheckedIn: true } : r));
                            setPpMessage({ type: 'error', text: `${g.fullName} already picked up.` });
                          } else {
                            setPpMessage({ type: 'error', text: err?.message || 'Failed to process.' });
                          }
                        } finally { setPpConfirming(null); }
                      }}
                      aria-label={`Confirm pickup for ${g.fullName}`}
                    >
                      {ppConfirming === g.guestRecordId ? '...' : 'Confirm'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delivery Queue — delivery window */}
      <button
        type="button"
        style={{
          ...actionBtnStyle,
          ...(showDeliveryQueue ? {
            background: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
            borderColor: '#fbbf24',
            boxShadow: '0 0 10px rgba(251, 191, 36, 0.3)',
          } : {}),
        }}
        onClick={() => { setShowDeliveryQueue((s) => !s); setShowPacketPickup(false); setShowGuestSearch(false); setShowTableSearch(false); setShowVendors(false); setShowJourney(false); setShowGateMonitor(false); }}
        aria-label="Delivery Queue"
      >
        <span style={iconSpanStyle} aria-hidden="true">🚢</span>
        <span>Delivery Queue</span>
      </button>

      {showDeliveryQueue && (
        <div style={searchContainerStyle}>
          <DeliveryQueueView />
        </div>
      )}

      {/* K-LOC — Logistics Operations Center */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={() => navigate('/logistics-center')}
        aria-label="K-LOC Logistics Operations Center"
      >
        <span style={iconSpanStyle} aria-hidden="true">🏢</span>
        <span>K-LOC</span>
      </button>

      {/* Monitor Gates — event selection + inline gate monitoring */}
      <button
        type="button"
        style={{
          ...actionBtnStyle,
          ...(showGateMonitor ? {
            background: 'linear-gradient(135deg, #1a6b3c 0%, #2e7d32 100%)',
            borderColor: '#4ade80',
            boxShadow: '0 0 12px rgba(74, 222, 128, 0.4)',
          } : {}),
        }}
        onClick={handleOpenGateMonitor}
        aria-label="Monitor Gates"
      >
        <span style={iconSpanStyle} aria-hidden="true">🚪</span>
        <span>Monitor Gates</span>
        {showGateMonitor && <span style={{ marginLeft: 'auto', fontSize: '0.7rem', background: '#4ade80', color: '#0f2d1f', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>LIVE</span>}
      </button>

      {showGateMonitor && (
        <div style={searchContainerStyle}>
          {gateEventsLoading && <p style={{ color: '#c9a84c', fontSize: '0.85rem', margin: 0 }}>Loading events...</p>}
          {!gateEventsLoading && !gateSelectedEventId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <p style={{ color: '#a0a0a0', fontSize: '0.82rem', margin: '0 0 4px 0' }}>Select an event to monitor gates:</p>
              {gateEvents.map((evt) => {
                const isLive = liveEventIds.has(evt.id);
                return (
                <button
                  key={evt.id}
                  type="button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '16px 18px',
                    border: isLive ? '1px solid #f87171' : '1px solid #2a5a3e',
                    borderRadius: '10px',
                    background: isLive ? '#7f1d1d' : '#0f1f17',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                  onClick={() => setGateSelectedEventId(evt.id)}
                  aria-label={`Monitor gates for ${evt.name}`}
                >
                  <span style={{ fontSize: '1.3rem' }}>🎪</span>
                  <div style={{ flex: 1 }}>
                    <div>{evt.name}</div>
                    {evt.date && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '2px' }}>
                        {(() => {
                          const d = evt.date.includes('T') ? evt.date.split('T')[0] : evt.date;
                          const [y, m, day] = d.split('-').map(Number);
                          return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        })()}
                      </div>
                    )}
                  </div>
                  {isLive && <span style={{ fontSize: '0.7rem', background: '#ef4444', color: '#fff', padding: '3px 8px', borderRadius: '10px', fontWeight: 700, flexShrink: 0 }}>LIVE</span>}
                </button>
                );
              })}
              {gateEvents.length === 0 && !gateEventsLoading && (
                <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>No events found.</p>
              )}
            </div>
          )}
          {gateSelectedEventId && (
            <div>
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: '#c9a84c', fontSize: '0.85rem', cursor: 'pointer', padding: '4px 0', marginBottom: '10px', fontWeight: 600 }}
                onClick={() => setGateSelectedEventId(null)}
                aria-label="Back to event selection"
              >
                ← Back to Events
              </button>
              <GateMonitoringView eventId={gateSelectedEventId} />
            </div>
          )}
        </div>
      )}

      {/* FAQ Lookup */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={() => navigate('/guest-questions?readonly=1')}
        aria-label="FAQ Lookup"
      >
        <span style={iconSpanStyle} aria-hidden="true">❓</span>
        <span>FAQ Lookup</span>
      </button>

      {/* Send Text */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={() => navigate('/send-text')}
        aria-label="Send Text Message"
      >
        <span style={iconSpanStyle} aria-hidden="true">📱</span>
        <span>Send Text</span>
      </button>

      {/* QR Code Directory */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={() => navigate('/qr-directory')}
        aria-label="QR Code Directory"
      >
        <span style={iconSpanStyle} aria-hidden="true">📲</span>
        <span>QR Code Directory</span>
      </button>

      {/* Vendor Directory — inline read-only table */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={handleOpenVendors}
        aria-label="Vendor Directory"
      >
        <span style={iconSpanStyle} aria-hidden="true">🏪</span>
        <span>Vendors</span>
      </button>

      {showVendors && (
        <div style={searchContainerStyle}>
          {vendorsLoading && <p style={{ color: '#c9a84c', fontSize: '0.85rem', margin: 0 }}>Loading vendors...</p>}
          {!vendorsLoading && vendors.length === 0 && vendorsLoaded && (
            <p style={{ color: '#c9a84c', fontSize: '0.85rem', margin: 0, padding: '12px', background: '#0f1f17', borderRadius: '8px' }}>No vendors found in the system. Add vendors on the Vendors page.</p>
          )}
          {!vendorsLoading && vendors.length > 0 && !selectedVendor && (() => {
            const CATEGORY_ICONS: Record<string, string> = { Catering: '🍽️', Photography: '📷', Entertainment: '🎶', Decor: '🎨', Florist: '💐', Transport: '🚐', Security: '🛡️', AV: '🔊', Rentals: '🪑', Other: '📦' };
            const categories = [...new Set(vendors.map(v => v.vendor.category || 'Other'))].sort();
            const events = [...new Set(vendors.map(v => v.event.name).filter(n => n && n !== '—'))].sort();
            const zones = [...new Set(vendors.map(v => v.zone).filter(Boolean))].sort() as string[];

            const filteredVendors = vendorDrilldown
              ? vendors.filter(v => {
                  if (vendorView === 'category') return (v.vendor.category || 'Other') === vendorDrilldown;
                  if (vendorView === 'event') return v.event.name === vendorDrilldown;
                  if (vendorView === 'zone') return v.zone === vendorDrilldown;
                  return true;
                })
              : vendors;

            return (
              <div>
                {/* Filter pills */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {(['all', 'category', 'event', 'zone'] as const).map((view) => {
                    const labels = { all: '📋 All', category: '📂 Category', event: '🎪 Event', zone: '📍 Zone' };
                    const isActive = vendorView === view && !vendorDrilldown;
                    return (
                      <button
                        key={view}
                        type="button"
                        style={{
                          padding: '6px 12px',
                          borderRadius: '16px',
                          border: isActive ? '1px solid #c9a84c' : '1px solid #2a5a3e',
                          background: isActive ? '#2a4a35' : '#0f1f17',
                          color: isActive ? '#c9a84c' : '#a0a0a0',
                          fontSize: '0.78rem',
                          fontWeight: isActive ? 700 : 500,
                          cursor: 'pointer',
                        }}
                        onClick={() => { setVendorView(view); setVendorDrilldown(null); }}
                        aria-pressed={isActive}
                      >
                        {labels[view]}
                      </button>
                    );
                  })}
                </div>

                {/* Drilldown back button */}
                {vendorDrilldown && (
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', color: '#c9a84c', fontSize: '0.82rem', cursor: 'pointer', padding: '0 0 8px', fontWeight: 600 }}
                    onClick={() => setVendorDrilldown(null)}
                    aria-label="Back"
                  >
                    ← Back to {vendorView === 'category' ? 'Categories' : vendorView === 'event' ? 'Events' : 'Zones'}
                  </button>
                )}

                {/* Category icons view */}
                {vendorView === 'category' && !vendorDrilldown && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '14px 8px', background: '#0f1f17', border: '1px solid #2a5a3e', borderRadius: '10px', cursor: 'pointer', color: '#e0e0e0' }}
                        onClick={() => setVendorDrilldown(cat)}
                        aria-label={`View ${cat} vendors`}
                      >
                        <span style={{ fontSize: '1.5rem' }}>{CATEGORY_ICONS[cat] || '📦'}</span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, textAlign: 'center' }}>{cat}</span>
                        <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{vendors.filter(v => (v.vendor.category || 'Other') === cat).length}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Event icons view */}
                {vendorView === 'event' && !vendorDrilldown && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {events.map((evt) => (
                      <button
                        key={evt}
                        type="button"
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '14px 8px', background: '#0f1f17', border: '1px solid #2a5a3e', borderRadius: '10px', cursor: 'pointer', color: '#e0e0e0' }}
                        onClick={() => setVendorDrilldown(evt)}
                        aria-label={`View vendors for ${evt}`}
                      >
                        <span style={{ fontSize: '1.5rem' }}>🎪</span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, textAlign: 'center' }}>{evt}</span>
                        <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{vendors.filter(v => v.event.name === evt).length}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Zone icons view */}
                {vendorView === 'zone' && !vendorDrilldown && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {zones.length === 0 && <p style={{ color: '#888', fontSize: '0.82rem', gridColumn: '1 / -1' }}>No zones assigned yet.</p>}
                    {zones.map((z) => (
                      <button
                        key={z}
                        type="button"
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '14px 8px', background: '#0f1f17', border: '1px solid #2a5a3e', borderRadius: '10px', cursor: 'pointer', color: '#e0e0e0' }}
                        onClick={() => setVendorDrilldown(z)}
                        aria-label={`View vendors in zone ${z}`}
                      >
                        <span style={{ fontSize: '1.5rem' }}>📍</span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, textAlign: 'center' }}>{z}</span>
                        <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{vendors.filter(v => v.zone === z).length}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Vendor list (All view or drilldown results) */}
                {(vendorView === 'all' || vendorDrilldown) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto' }}>
                    {filteredVendors.map((v) => (
                      <div
                        key={v.id}
                        style={{ padding: '12px 14px', background: '#0f1f17', borderRadius: '10px', border: '1px solid #1a3d2a' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>{v.vendor.businessName}</div>
                            {v.vendor.category && <div style={{ fontSize: '0.72rem', color: '#c9a84c', marginTop: '2px' }}>{v.vendor.category}</div>}
                          </div>
                          {v.boothNumber && <span style={{ fontSize: '0.7rem', background: '#2a4a35', color: '#c9a84c', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>Booth {v.boothNumber}</span>}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '8px', fontSize: '0.8rem', color: '#ccc' }}>
                          {v.vendor.contactPerson && <div>👤 {v.vendor.contactPerson}</div>}
                          {v.vendor.email && <div>✉️ <a href={`mailto:${v.vendor.email}`} style={{ color: '#6ee7b7', textDecoration: 'none' }}>{v.vendor.email}</a></div>}
                          {v.vendor.phone && <div>📞 <a href={`tel:${v.vendor.phone}`} style={{ color: '#6ee7b7', textDecoration: 'none' }}>{v.vendor.phone}</a></div>}
                          {v.event.name !== '—' && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '2px' }}>🎪 {v.event.name}{v.zone ? ` • Zone: ${v.zone}` : ''}</div>}
                          {v.description && <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>{v.description}</div>}
                        </div>
                      </div>
                    ))}
                    {filteredVendors.length === 0 && (
                      <p style={{ color: '#888', fontSize: '0.82rem', padding: '12px', margin: 0 }}>No vendors in this {vendorView}.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {selectedVendor && (
            <div style={{ background: '#0f1f17', borderRadius: '10px', padding: '14px', border: '1px solid #2a4a35' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <strong style={{ fontSize: '1rem', color: '#fff' }}>{selectedVendor.businessName}</strong>
                <button type="button" style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.1rem' }} onClick={() => setSelectedVendor(null)} aria-label="Close vendor detail">✕</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '0.85rem', color: '#ccc' }}>
                {selectedVendor.category && <div>📂 <strong>Category:</strong> {selectedVendor.category}</div>}
                {selectedVendor.contactPerson && <div>👤 <strong>Contact:</strong> {selectedVendor.contactPerson}</div>}
                {selectedVendor.email && <div>✉️ <strong>Email:</strong> {selectedVendor.email}</div>}
                {selectedVendor.phone && <div>📞 <strong>Phone:</strong> {selectedVendor.phone}</div>}
                {selectedVendor.mailingAddress && <div>📍 <strong>Address:</strong> {selectedVendor.mailingAddress}</div>}
                {selectedVendor.eventRegistrations && selectedVendor.eventRegistrations.length > 0 && (
                  <div style={{ marginTop: '8px', borderTop: '1px solid #2a4a35', paddingTop: '8px' }}>
                    <strong style={{ color: '#c9a84c', fontSize: '0.8rem' }}>Event Assignments:</strong>
                    {selectedVendor.eventRegistrations.map((reg) => (
                      <div key={reg.id} style={{ marginTop: '4px', padding: '6px 8px', background: '#1a2e23', borderRadius: '6px', fontSize: '0.8rem' }}>
                        <div>🎉 {reg.event.name} — <span style={{ color: reg.status === 'Confirmed' ? '#6ee7b7' : '#fbbf24' }}>{reg.status}</span></div>
                        {reg.boothNumber && <div style={{ marginTop: '2px' }}>🏪 Booth {reg.boothNumber}{reg.zone ? ` • Zone: ${reg.zone}` : ''}</div>}
                        {reg.description && <div style={{ marginTop: '2px', color: '#9ca3af' }}>{reg.description}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table Management Reports — mobile optimized */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={async () => {
          const toggling = !showReports;
          setShowReports(toggling);
          setShowGuestSearch(false); setShowTableSearch(false); setShowVendors(false); setShowJourney(false); setShowGateMonitor(false); setShowPacketPickup(false);
          setReportsView('menu'); setSelectedReportChapter(null);
          if (toggling && chapterDistData.length === 0) {
            setReportsLoading(true);
            try {
              const data = await apiClient<typeof chapterDistData>('/table-management/reports/chapter-distribution');
              setChapterDistData(Array.isArray(data) ? data : []);
            } catch { setChapterDistData([]); }
            finally { setReportsLoading(false); }
          }
        }}
        aria-label="Table Management Reports"
      >
        <span style={iconSpanStyle} aria-hidden="true">📊</span>
        <span>Reports</span>
      </button>

      {showReports && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px', background: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          {reportsLoading && <p style={{ color: '#1a6b3c', fontSize: '0.85rem', margin: 0 }}>Loading reports...</p>}

          {!reportsLoading && reportsView === 'menu' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button type="button" style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '16px 18px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f9fafb', color: '#0f2d1f', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }} onClick={() => setReportsView('summary')}>
                <span style={{ fontSize: '1.3rem' }}>📋</span>
                <div><div>Summary Chapter Distribution</div><div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>{chapterDistData.length} chapters</div></div>
              </button>
              <button type="button" style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '16px 18px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f9fafb', color: '#0f2d1f', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }} onClick={() => setReportsView('detail')}>
                <span style={{ fontSize: '1.3rem' }}>🔍</span>
                <div><div>Detail Chapter Distribution</div><div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>Select a chapter to view guests</div></div>
              </button>
              <button type="button" style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '16px 18px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f9fafb', color: '#0f2d1f', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }} onClick={async () => {
                setReportsView('pickup-stats');
                if (!pickupStats) {
                  setReportsLoading(true);
                  try { const data = await apiClient<typeof pickupStats>('/packet-pickup/stats'); setPickupStats(data); }
                  catch { setPickupStats({ byLocation: {}, totalPickups: 0, totalExpected: 0 }); }
                  finally { setReportsLoading(false); }
                }
              }}>
                <span style={{ fontSize: '1.3rem' }}>📦</span>
                <div><div>Packet Pickup Stats</div><div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>Pickups by location</div></div>
              </button>
              <button type="button" style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '16px 18px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f9fafb', color: '#0f2d1f', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }} onClick={async () => {
                setReportsView('key-members');
                if (!keyMembersData) {
                  setReportsLoading(true);
                  try {
                    const data = await apiClient<{ keyMembers: Array<{ name: string; role: string; chapter: string; tableName: string | null; tableNumber: number | null; zoneName: string | null; isSeated: boolean; hasTicket: boolean }> }>('/seating-intelligence/insights-report');
                    setKeyMembersData(data.keyMembers);
                  } catch { setKeyMembersData([]); }
                  finally { setReportsLoading(false); }
                }
              }}>
                <span style={{ fontSize: '1.3rem' }}>👑</span>
                <div><div>Presidents &amp; Heritage</div><div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>Chapter Presidents &amp; Heritage Members</div></div>
              </button>
              <button type="button" style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '16px 18px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f9fafb', color: '#0f2d1f', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }} onClick={() => setReportsView('visual-map')}>
                <span style={{ fontSize: '1.3rem' }}>🪑</span>
                <div><div>Visual Table Map</div><div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>Circular seat visualization by zone</div></div>
              </button>
              <button type="button" style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '16px 18px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f9fafb', color: '#0f2d1f', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }} onClick={async () => {
                setReportsView('table-requests');
                if (!tableRequestsData) {
                  setReportsLoading(true);
                  try {
                    const data = await apiClient<Array<{ id: string; name: string; status: string; members: Array<{ guestId: string | null; guest: { seat?: Array<{ seatNumber?: number; table: { tableNumber: number } }> } | null }>; _count: { members: number } }>>('/table-reservation-requests');
                    const mapped = data.map((r) => {
                      const linkedMembers = r.members.filter((m) => m.guestId);
                      const seatedMembers = linkedMembers.filter((m) => m.guest?.seat && m.guest.seat.length > 0);
                      const tables = [...new Set(seatedMembers.map((m) => m.guest!.seat![0].table.tableNumber))].sort((a, b) => a - b);
                      const seats = seatedMembers.map((m) => m.guest!.seat![0].seatNumber || 0).filter((s) => s > 0).sort((a, b) => a - b);
                      const allSeated = linkedMembers.length > 0 && seatedMembers.length === linkedMembers.length;
                      return { id: r.id, name: r.name, status: r.status, memberCount: r._count.members, tables, seats, allSeated };
                    });
                    setTableRequestsData(mapped);
                  } catch { setTableRequestsData([]); }
                  finally { setReportsLoading(false); }
                }
              }}>
                <span style={{ fontSize: '1.3rem' }}>📝</span>
                <div><div>Table Requests</div><div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>All requests with assigned tables</div></div>
              </button>
            </div>
          )}

          {/* Summary view */}
          {!reportsLoading && reportsView === 'summary' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <button type="button" style={{ background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.82rem', cursor: 'pointer', padding: '0', fontWeight: 600 }} onClick={() => setReportsView('menu')}>← Back</button>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: reportDisplayMode === 'cards' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: reportDisplayMode === 'cards' ? '#e8f5e9' : '#fff', color: reportDisplayMode === 'cards' ? '#1a6b3c' : '#6b7280', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setReportDisplayMode('cards')}>Cards</button>
                  <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: reportDisplayMode === 'table' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: reportDisplayMode === 'table' ? '#e8f5e9' : '#fff', color: reportDisplayMode === 'table' ? '#1a6b3c' : '#6b7280', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setReportDisplayMode('table')}>Table</button>
                </div>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px' }}>{chapterDistData.length} chapters • {chapterDistData.reduce((s, r) => s + r.totalGuests, 0)} guests</div>

              {/* Cards mode */}
              {reportDisplayMode === 'cards' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '420px', overflowY: 'auto' }}>
                  {chapterDistData.map((row) => (
                    <div key={row.chapter} style={{ padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#0f2d1f', fontSize: '0.88rem' }}>{row.chapter}</div>
                          {row.inceptionYear && <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>Est. {row.inceptionYear}</div>}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a6b3c' }}>{row.totalGuests}</div>
                          <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>guests</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '0.72rem' }}>
                        <span style={{ color: '#4caf50' }}>✓ {row.seatedGuests} seated</span>
                        {row.unseatedGuests > 0 && <span style={{ color: '#ef5350' }}>○ {row.unseatedGuests} unseated</span>}
                        <span style={{ color: '#6b7280' }}>🪑 {row.tableCount} tables</span>
                        <span style={{ color: '#6b7280' }}>📍 {row.zoneCount} zones</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table mode */}
              {reportDisplayMode === 'table' && (
                <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', minWidth: '420px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #2a5a3e' }}>
                        <th style={{ textAlign: 'left', padding: '6px', color: '#1a6b3c', fontWeight: 600 }}>Chapter</th>
                        <th style={{ textAlign: 'center', padding: '6px', color: '#1a6b3c', fontWeight: 600 }}>Est.</th>
                        <th style={{ textAlign: 'center', padding: '6px', color: '#1a6b3c', fontWeight: 600 }}>Total</th>
                        <th style={{ textAlign: 'center', padding: '6px', color: '#4caf50', fontWeight: 600 }}>Seated</th>
                        <th style={{ textAlign: 'center', padding: '6px', color: '#ef5350', fontWeight: 600 }}>Unseated</th>
                        <th style={{ textAlign: 'center', padding: '6px', color: '#6b7280', fontWeight: 600 }}>Tables</th>
                        <th style={{ textAlign: 'center', padding: '6px', color: '#6b7280', fontWeight: 600 }}>Zones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chapterDistData.map((row) => (
                        <tr key={row.chapter} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '6px', color: '#0f2d1f', fontWeight: 500 }}>{row.chapter}</td>
                          <td style={{ textAlign: 'center', padding: '6px', color: '#6b7280' }}>{row.inceptionYear || '—'}</td>
                          <td style={{ textAlign: 'center', padding: '6px', color: '#1a6b3c', fontWeight: 700 }}>{row.totalGuests}</td>
                          <td style={{ textAlign: 'center', padding: '6px', color: '#4caf50' }}>{row.seatedGuests}</td>
                          <td style={{ textAlign: 'center', padding: '6px', color: row.unseatedGuests > 0 ? '#ef5350' : '#666' }}>{row.unseatedGuests}</td>
                          <td style={{ textAlign: 'center', padding: '6px', color: '#6b7280' }}>{row.tableCount}</td>
                          <td style={{ textAlign: 'center', padding: '6px', color: '#6b7280' }}>{row.zoneCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Detail view — chapter selection */}
          {!reportsLoading && reportsView === 'detail' && !selectedReportChapter && (
            <div>
              <button type="button" style={{ background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.82rem', cursor: 'pointer', padding: '0 0 10px', fontWeight: 600 }} onClick={() => setReportsView('menu')}>← Back</button>
              <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0 0 10px' }}>Select a chapter:</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                {chapterDistData.map((row) => (
                  <button key={row.chapter} type="button" style={{ padding: '12px 10px', background: '#f9fafb', border: '1px solid #2a5a3e', borderRadius: '10px', cursor: 'pointer', color: '#0f2d1f', textAlign: 'center' }} onClick={() => setSelectedReportChapter(row.chapter)}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{row.chapter}</div>
                    <div style={{ fontSize: '0.7rem', color: '#1a6b3c', marginTop: '2px' }}>{row.totalGuests} guests</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Detail view — guest list for selected chapter */}
          {!reportsLoading && reportsView === 'detail' && selectedReportChapter && (() => {
            const chapterData = chapterDistData.find(r => r.chapter === selectedReportChapter);
            if (!chapterData) return null;
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <button type="button" style={{ background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.82rem', cursor: 'pointer', padding: '0', fontWeight: 600 }} onClick={() => setSelectedReportChapter(null)}>← Back to Chapters</button>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: reportDisplayMode === 'cards' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: reportDisplayMode === 'cards' ? '#e8f5e9' : '#fff', color: reportDisplayMode === 'cards' ? '#1a6b3c' : '#6b7280', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setReportDisplayMode('cards')}>Cards</button>
                    <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: reportDisplayMode === 'table' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: reportDisplayMode === 'table' ? '#e8f5e9' : '#fff', color: reportDisplayMode === 'table' ? '#1a6b3c' : '#6b7280', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setReportDisplayMode('table')}>Table</button>
                  </div>
                </div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f2d1f', marginBottom: '4px' }}>{chapterData.chapter}</div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '10px' }}>{chapterData.totalGuests} guests • {chapterData.seatedGuests} seated • {chapterData.tableCount} tables</div>

                {/* Cards mode */}
                {reportDisplayMode === 'cards' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '400px', overflowY: 'auto' }}>
                    {chapterData.guests.map((g, idx) => (
                      <div key={idx} style={{ padding: '8px 10px', background: g.isSeated ? '#0f1f17' : '#1a1a12', borderRadius: '6px', border: `1px solid ${g.isSeated ? '#1a3d2a' : '#3d3a1a'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, color: '#0f2d1f', fontSize: '0.84rem' }}>{g.fullName}</span>
                          {g.isSeated ? <span style={{ fontSize: '0.65rem', color: '#4caf50', fontWeight: 600 }}>Seated</span> : <span style={{ fontSize: '0.65rem', color: '#f59e0b' }}>No seat</span>}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px', fontSize: '0.72rem', color: '#6b7280' }}>
                          {g.ticketNumber && <span>🎫 {g.ticketNumber}</span>}
                          {g.orderReferenceId && <span>📦 {g.orderReferenceId}</span>}
                          {g.tableName && <span>🪑 {g.tableName}</span>}
                          {g.tableNumber != null && <span>T{g.tableNumber}</span>}
                          {g.seatNumber != null && <span>S{g.seatNumber}</span>}
                          {g.zoneName && <span>📍 {g.zoneName}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Table mode */}
                {reportDisplayMode === 'table' && (
                  <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem', minWidth: '500px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #2a5a3e' }}>
                          <th style={{ textAlign: 'left', padding: '5px', color: '#1a6b3c', fontWeight: 600 }}>Guest</th>
                          <th style={{ textAlign: 'left', padding: '5px', color: '#1a6b3c', fontWeight: 600 }}>Order</th>
                          <th style={{ textAlign: 'left', padding: '5px', color: '#1a6b3c', fontWeight: 600 }}>Ticket</th>
                          <th style={{ textAlign: 'left', padding: '5px', color: '#1a6b3c', fontWeight: 600 }}>Table</th>
                          <th style={{ textAlign: 'center', padding: '5px', color: '#1a6b3c', fontWeight: 600 }}>Seat</th>
                          <th style={{ textAlign: 'left', padding: '5px', color: '#1a6b3c', fontWeight: 600 }}>Zone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chapterData.guests.map((g, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb', background: g.isSeated ? '' : '#1a1a0f' }}>
                            <td style={{ padding: '5px', color: '#0f2d1f', fontWeight: 500 }}>{g.fullName}</td>
                            <td style={{ padding: '5px', color: g.orderReferenceId ? '#60a5fa' : '#555', fontFamily: 'monospace' }}>{g.orderReferenceId || '—'}</td>
                            <td style={{ padding: '5px', color: '#6b7280', fontFamily: 'monospace' }}>{g.ticketNumber || '—'}</td>
                            <td style={{ padding: '5px', color: '#6b7280' }}>{g.tableName || (g.tableNumber != null ? `T${g.tableNumber}` : '—')}</td>
                            <td style={{ textAlign: 'center', padding: '5px', color: '#6b7280' }}>{g.seatNumber ?? '—'}</td>
                            <td style={{ padding: '5px', color: '#6b7280' }}>{g.zoneName || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Packet Pickup Stats view */}
          {!reportsLoading && reportsView === 'pickup-stats' && pickupStats && (
            <div>
              <button type="button" style={{ background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.82rem', cursor: 'pointer', padding: '0 0 10px', fontWeight: 600 }} onClick={() => setReportsView('menu')}>← Back</button>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f2d1f', marginBottom: '12px' }}>📦 Packet Pickup Stats</div>

              {/* Progress bar */}
              <div style={{ background: '#f0f7f4', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                  <span style={{ fontSize: '2rem', fontWeight: 800, color: '#1a6b3c' }}>{pickupStats.totalPickups}</span>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>of {pickupStats.totalExpected} expected</span>
                </div>
                <div style={{ height: '8px', background: '#f9fafb', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: '#c9a84c', borderRadius: '4px', width: `${pickupStats.totalExpected > 0 ? Math.min(100, Math.round((pickupStats.totalPickups / pickupStats.totalExpected) * 100)) : 0}%`, transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px', textAlign: 'right' }}>
                  {pickupStats.totalExpected > 0 ? Math.round((pickupStats.totalPickups / pickupStats.totalExpected) * 100) : 0}% processed
                </div>
              </div>

              {/* By location breakdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { key: 'Hotel', icon: '🏨', label: 'Hotel' },
                  { key: 'Symposium', icon: '🎓', label: 'Symposium' },
                  { key: 'Meet_and_Greet', icon: '🤝', label: 'Meet & Greet' },
                  { key: 'Ball_Door', icon: '🚪', label: 'Ball Door' },
                ].map(({ key, icon, label }) => {
                  const count = (pickupStats.byLocation as Record<string, number>)[key] || 0;
                  return (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '1rem', color: '#e0e0e0' }}>{icon} {label}</span>
                      <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1a6b3c' }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Key Members view */}
          {!reportsLoading && reportsView === 'key-members' && keyMembersData && (() => {
            const roleFiltered = keyMembersData.filter((m) => m.role === 'President' || m.role === 'Heritage');
            const filtered = keyMembersTicketFilter === 'all' ? roleFiltered
              : keyMembersTicketFilter === 'with-ticket' ? roleFiltered.filter((m) => m.hasTicket)
              : roleFiltered.filter((m) => !m.hasTicket);
            return (
            <div>
              <button type="button" style={{ background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.82rem', cursor: 'pointer', padding: '0 0 10px', fontWeight: 600 }} onClick={() => setReportsView('menu')}>← Back</button>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f2d1f' }}>👑 Presidents &amp; Heritage</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: keyMembersDisplay === 'table' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: keyMembersDisplay === 'table' ? '#e8f5e9' : '#fff', color: keyMembersDisplay === 'table' ? '#1a6b3c' : '#6b7280', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setKeyMembersDisplay('table')}>Table</button>
                  <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: keyMembersDisplay === 'cards' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: keyMembersDisplay === 'cards' ? '#e8f5e9' : '#fff', color: keyMembersDisplay === 'cards' ? '#1a6b3c' : '#6b7280', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setKeyMembersDisplay('cards')}>Cards</button>
                </div>
              </div>

              {/* Ticket filter */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
                <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: keyMembersTicketFilter === 'all' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: keyMembersTicketFilter === 'all' ? '#e8f5e9' : '#fff', color: keyMembersTicketFilter === 'all' ? '#1a6b3c' : '#6b7280', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setKeyMembersTicketFilter('all')}>All</button>
                <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: keyMembersTicketFilter === 'with-ticket' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: keyMembersTicketFilter === 'with-ticket' ? '#e8f5e9' : '#fff', color: keyMembersTicketFilter === 'with-ticket' ? '#1a6b3c' : '#6b7280', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setKeyMembersTicketFilter('with-ticket')}>Has Ticket</button>
                <button type="button" style={{ padding: '4px 10px', borderRadius: '12px', border: keyMembersTicketFilter === 'no-ticket' ? '1px solid #1a6b3c' : '1px solid #d1d5db', background: keyMembersTicketFilter === 'no-ticket' ? '#e8f5e9' : '#fff', color: keyMembersTicketFilter === 'no-ticket' ? '#1a6b3c' : '#6b7280', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setKeyMembersTicketFilter('no-ticket')}>No Ticket</button>
              </div>

              <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '12px' }}>
                {filtered.length} members • {filtered.filter((m) => m.isSeated).length} seated • {filtered.filter((m) => !m.isSeated).length} not seated
              </div>

              {/* Table view */}
              {keyMembersDisplay === 'table' && (
                <div style={{ overflowX: 'auto', maxHeight: '480px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }} aria-label="Key members table">
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase' }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase' }}>Chapter</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase' }}>Status</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase' }}>Table</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((km, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: km.isSeated ? '' : '#fef2f2' }}>
                          <td style={{ padding: '8px', color: '#0f2d1f', fontWeight: 500 }}>
                            {km.name}
                            <span style={{ display: 'block', fontSize: '0.65rem', color: '#6b7280', marginTop: '1px' }}>{km.role}</span>
                          </td>
                          <td style={{ padding: '8px', color: '#333', fontSize: '0.76rem' }}>{km.chapter}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', background: km.isSeated ? '#e8f5e9' : '#fdecea', color: km.isSeated ? '#2e7d32' : '#d32f2f' }}>
                              {km.isSeated ? '✓' : '○'}
                            </span>
                          </td>
                          <td style={{ padding: '8px', color: km.isSeated ? '#1a6b3c' : '#999', fontSize: '0.76rem' }}>{km.tableName || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Cards view */}
              {keyMembersDisplay === 'cards' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '480px', overflowY: 'auto' }}>
                  {filtered.map((km, idx) => (
                    <div key={idx} style={{ padding: '12px 14px', background: km.isSeated ? '#f9fafb' : '#fef2f2', borderRadius: '10px', border: `1px solid ${km.isSeated ? '#d1e7dd' : '#fecaca'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: '#0f2d1f', fontSize: '0.9rem' }}>{km.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>{km.chapter}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: km.role === 'President' ? '#e8eaf6' : '#f3e5f5', color: km.role === 'President' ? '#283593' : '#6a1b9a' }}>{km.role}</span>
                          <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: km.isSeated ? '#e8f5e9' : '#fdecea', color: km.isSeated ? '#2e7d32' : '#d32f2f' }}>
                            {km.isSeated ? '✓ Seated' : '○ Not Seated'}
                          </span>
                        </div>
                      </div>
                      {km.isSeated && km.tableName && (
                        <div style={{ marginTop: '8px', padding: '6px 10px', background: '#f5f5f5', borderRadius: '6px', fontSize: '0.78rem', color: '#1a6b3c' }}>
                          🪑 {km.tableName} • {km.zoneName}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })()}

          {/* Visual Table Map view */}
          {!reportsLoading && reportsView === 'visual-map' && (
            <div>
              <button type="button" style={{ background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.82rem', cursor: 'pointer', padding: '0 0 10px', fontWeight: 600 }} onClick={() => setReportsView('menu')}>← Back</button>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f2d1f', marginBottom: '10px' }}>🪑 Visual Table Map</div>
              <MobileVisualTableMap />
            </div>
          )}

          {/* Table Requests view */}
          {!reportsLoading && reportsView === 'table-requests' && (
            <div>
              <button type="button" style={{ background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.82rem', cursor: 'pointer', padding: '0 0 10px', fontWeight: 600 }} onClick={() => setReportsView('menu')}>← Back</button>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f2d1f', marginBottom: '10px' }}>📝 Table Requests Report</div>
              {tableRequestsData && tableRequestsData.length === 0 && (
                <p style={{ color: '#888', fontSize: '0.85rem' }}>No table requests found.</p>
              )}
              {tableRequestsData && tableRequestsData.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {tableRequestsData.map((req) => (
                    <div key={req.id} style={{ padding: '12px', background: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f2d1f' }}>{req.name}</span>
                        <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', backgroundColor: req.status === 'Fulfilled' ? '#e8f5e9' : req.status === 'GroupCreated' ? '#e8f5e9' : req.status === 'InProgress' ? '#fff3e0' : req.status === 'Inactive' ? '#f3f4f6' : '#e3f2fd', color: req.status === 'Fulfilled' ? '#1b5e20' : req.status === 'GroupCreated' ? '#2e7d32' : req.status === 'InProgress' ? '#e65100' : req.status === 'Inactive' ? '#6b7280' : '#1565c0' }}>{req.status}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '6px' }}>{req.memberCount} member{req.memberCount !== 1 ? 's' : ''}</div>
                      {req.tables.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                          {req.tables.map((t) => (
                            <div key={t} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#1a6b3c', color: '#fff', fontSize: '1.4rem', fontWeight: 800, boxShadow: '0 3px 10px rgba(26,107,60,0.3)' }}>{t}</div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.75rem', color: '#d97706', fontStyle: 'italic', textAlign: 'center' }}>Not yet seated</div>
                      )}
                      {req.allSeated && req.seats.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '6px', textAlign: 'center' }}>Seat Numbers</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                            {req.seats.map((s, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '50px', height: '50px', padding: '0 14px', borderRadius: '25px', backgroundColor: '#0277bd', color: '#fff', fontSize: '1.3rem', fontWeight: 800, boxShadow: '0 3px 10px rgba(2,119,189,0.3)' }}>{s}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* GX Team Console — Journey Card (mobile-optimized) */}
      <button
        type="button"
        style={actionBtnStyle}
        onClick={handleOpenJourney}
        aria-label="GX Journey"
      >
        <span style={iconSpanStyle} aria-hidden="true">🎯</span>
        <span>GX Journey</span>
      </button>

      {showJourney && (
        <div style={searchContainerStyle}>
          {journeyLoading && <p style={{ color: '#1a6b3c', fontSize: '0.85rem', margin: 0 }}>Loading tasks...</p>}
          {!journeyLoading && journeyTasks.length === 0 && (
            <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>No weekend tasks found.</p>
          )}
          {!journeyLoading && journeyTasks.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '420px', overflowY: 'auto' }}>
              <div style={{ fontSize: '0.75rem', color: '#1a6b3c', fontWeight: 600, marginBottom: '2px' }}>
                {journeyTasks.length} weekend task{journeyTasks.length !== 1 ? 's' : ''}
              </div>
              {journeyTasks.map((task) => (
                <div
                  key={task.id}
                  style={{ padding: '10px 12px', background: '#1e3a2b', borderRadius: '8px', borderLeft: `3px solid ${task.priority === 'High' ? '#ef4444' : task.priority === 'Medium' ? '#f59e0b' : '#3b82f6'}` }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f2d1f', wordBreak: 'break-word' }}>{task.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px', wordBreak: 'break-word' }}>
                        {task.eventName}{task.eventTime ? ` · ${task.eventTime}` : ''}
                      </div>
                      {task.dateLabel && (
                        <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>📅 {task.dateLabel}</div>
                      )}
                    </div>
                    <span style={{ flexShrink: 0, fontSize: '0.65rem', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', background: task.status === 'Done' ? '#065f46' : task.status === 'InProgress' ? '#1e3a5f' : '#3b3b3b', color: task.status === 'Done' ? '#6ee7b7' : task.status === 'InProgress' ? '#93c5fd' : '#d1d5db' }}>
                      {task.status === 'InProgress' ? 'In Progress' : task.status}
                    </span>
                  </div>
                  {task.subcommittee && (
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '4px' }}>👥 {task.subcommittee}</div>
                  )}
                </div>
              ))}
              <button
                type="button"
                style={{ ...actionBtnStyle, justifyContent: 'center', padding: '12px', minHeight: 'unset', fontSize: '0.85rem', marginTop: '4px' }}
                onClick={() => navigate('/gx-console')}
                aria-label="Open full GX Console"
              >
                Open Full Console →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
